import { format } from 'date-fns'
import type { FieldNamesMarkedBoolean } from 'react-hook-form'
import { PAYMENT_FIELDS } from '../constants'
import { formatPhoneDisplay } from './formatPhone'
import type { PropertyConfigSubTab, PropertyEditFormValues, PropertyTab } from '../types'

export { PROPERTY_EDIT_ADDRESS_WARNING } from '../constants'

/** Maps API `listed_ids` (objects or numbers) to numeric ids. */
export function mapPropertyListedIds(raw: unknown): number[] {
  if (!Array.isArray(raw)) return []
  return raw.map((x: unknown) =>
    typeof x === 'object' && x !== null && 'org_id' in x
      ? (x as { org_id?: number }).org_id ?? 0
      : Number(x),
  )
}

/** API / radios may use 0 | 1 | "0" | "1" | boolean — normalize for the edit form. */
export function coerceAllowDynamicAccessFee(v: unknown): 0 | 1 {
  return v === 1 || v === '1' || v === true ? 1 : 0
}

/**
 * Executive Summary fields merge into the same POST payload as Angular.
 * Clear `time_frame_period` when eviction is "1".
 */
export function buildExecutiveSummarySnapshotFromProperty(
  p: Record<string, unknown> | null,
): Record<string, string> {
  const out: Record<string, string> = {
    income_requirement_criteria: String(p?.income_requirement_criteria ?? ''),
    allow_late_rent: String(p?.allow_late_rent ?? ''),
    eviction_credit_report: String(p?.eviction_credit_report ?? ''),
    time_frame_period: String(p?.time_frame_period ?? ''),
    allow_applicant_move: String(p?.allow_applicant_move ?? ''),
    applicant_purchase_policy: String(p?.applicant_purchase_policy ?? ''),
    main_contact_person: String(p?.main_contact_person ?? ''),
    special_note: String(p?.special_note ?? ''),
  }
  if (out.eviction_credit_report === '1') out.time_frame_period = ''
  return out
}

export function formatAuditAt(value: unknown): string {
  if (value == null || value === '') return '—'
  let ms: number
  if (typeof value === 'number' && value < 1e12) {
    ms = value * 1000
  } else if (typeof value === 'string' && /^\d{9,12}$/.test(value.trim())) {
    ms = Number(value.trim()) * 1000
  } else {
    ms = new Date(value as string | number).getTime()
  }
  const d = new Date(ms)
  if (Number.isNaN(d.getTime())) return String(value)
  return format(d, 'MM/dd/yyyy hh:mm a')
}

/** Determine which tab a form field belongs to for validation error auto-switch. */
export function getTabForField(fieldName: string): PropertyTab {
  if (fieldName === 'email_frequency') return 'communicationsinfo'
  return 'propertyinfo'
}

/** Angular `submitForm` `formEdited`: company id change or any non–company field dirty. */
export function computeFormEdited(
  values: PropertyEditFormValues,
  dirty: FieldNamesMarkedBoolean<PropertyEditFormValues>,
  originalCompanyId: string | number,
): boolean {
  const orig = String(originalCompanyId)
  if (String(values.fk_company_id) !== orig) return true
  for (const name of Object.keys(dirty) as (keyof PropertyEditFormValues)[]) {
    if (name === 'fk_company_id') continue
    const mark = dirty[name]
    if (mark === true || (Array.isArray(mark) && mark.some(Boolean))) return true
  }
  return false
}

/**
 * Angular `canViewConfigTab` — Config hosts the Yardi / Product / Model sub-tabs.
 * Keep `property_config_view` so existing React roles that only had that key still see Config.
 */
export function canViewPropertyConfigTab(hasPolicy: (keys: string[]) => boolean): boolean {
  return (
    hasPolicy(['property_config_view']) ||
    hasPolicy(['yardi_config']) ||
    hasPolicy(['property_productconfig_view']) ||
    hasPolicy(['property_modelconfig_view'])
  )
}

/** Angular `isConfigSubTabVisible` — same policy per sub-tab as the old top-level tabs. */
export function isPropertyConfigSubTabVisible(
  subTab: PropertyConfigSubTab,
  hasPolicy: (keys: string[]) => boolean,
): boolean {
  if (subTab === 'yardi') return hasPolicy(['yardi_config'])
  // Product: view policy only — content still requires `allow_product_config` (unchanged from the old tab).
  if (subTab === 'product') return hasPolicy(['property_productconfig_view'])
  return hasPolicy(['property_modelconfig_view'])
}

/** Angular `getDefaultConfigSubTab` — Yardi → Product → Model. */
export function getDefaultPropertyConfigSubTab(
  hasPolicy: (keys: string[]) => boolean,
): PropertyConfigSubTab {
  if (isPropertyConfigSubTabVisible('yardi', hasPolicy)) return 'yardi'
  if (isPropertyConfigSubTabVisible('product', hasPolicy)) return 'product'
  if (isPropertyConfigSubTabVisible('model', hasPolicy)) return 'model'
  return 'yardi'
}

/** Angular property-edit tabs: gated only by `*_view` policies (no write-access fallback). */
export function isPropertyTabVisible(
  tab: PropertyTab,
  hasPolicy: (keys: string[]) => boolean,
): boolean {
  switch (tab) {
    case 'propertyinfo':
      return hasPolicy(['property_info_view'])
    case 'executivesummary':
      return hasPolicy(['property_executive_summary_view'])
    case 'contactsinfo':
      return hasPolicy(['property_contact_view'])
    case 'communicationsinfo':
      return hasPolicy(['property_communication_view'])
    case 'bankinginfo':
      return hasPolicy(['property_payment_view'])
    case 'integrationsinfo':
      return hasPolicy(['property_integration_view'])
    case 'activityloginfo':
      return hasPolicy(['property_acitvitylog_view'])
    case 'configinfo':
      return canViewPropertyConfigTab(hasPolicy)
    default:
      return false
  }
}

/** First tab the user may open — same priority as Angular `setFirstVisibleTab`. */
export function getFirstVisibleTab(hasPolicy: (keys: string[]) => boolean): PropertyTab {
  const order: PropertyTab[] = [
    'propertyinfo',
    'contactsinfo',
    'communicationsinfo',
    'bankinginfo',
    'executivesummary',
    'integrationsinfo',
    'activityloginfo',
    'configinfo',
  ]
  for (const t of order) {
    if (isPropertyTabVisible(t, hasPolicy)) {
      return t
    }
  }
  return 'propertyinfo'
}

/** Map GET `/api/property/:id` response to react-hook-form values. */
export function mapPropertyToFormValues(p: Record<string, unknown>): PropertyEditFormValues {
  const companyId = p.fk_company_id ?? p.company_id ?? ''
  return {
    fk_company_id: companyId != null ? String(companyId) : '',
    property_name: p.property_name != null ? String(p.property_name) : '',
    property_alias_name: p.property_alias_name != null ? String(p.property_alias_name) : '',
    aln_ref_id: p.aln_ref_id != null ? String(p.aln_ref_id) : '',
    contact_name: p.contact_name != null ? String(p.contact_name) : '',
    contact_phone: formatPhoneDisplay(p.contact_phone as string | undefined),
    property_office_phone: formatPhoneDisplay(p.property_office_phone as string | undefined),
    property_address: p.property_address != null ? String(p.property_address) : '',
    property_address2: p.property_address2 != null ? String(p.property_address2) : '',
    property_city: p.property_city != null ? String(p.property_city) : '',
    property_state: p.property_state != null ? String(p.property_state) : '',
    property_zipcode: p.property_zipcode != null ? String(p.property_zipcode) : '',
    is_mailing_address: p.is_mailing_address === '1',
    mailing_property_address:
      p.mailing_property_address != null ? String(p.mailing_property_address) : '',
    mailing_property_address2:
      p.mailing_property_address2 != null ? String(p.mailing_property_address2) : '',
    mailing_property_city: p.mailing_property_city != null ? String(p.mailing_property_city) : '',
    mailing_property_state: p.mailing_property_state != null ? String(p.mailing_property_state) : '',
    mailing_property_zipcode:
      p.mailing_property_zipcode != null ? String(p.mailing_property_zipcode) : '',
    units_count: p.units_count != null ? String(p.units_count) : '',
    listed: p.listed != null ? String(p.listed) : '1',
    listed_ids: mapPropertyListedIds(p.listed_ids),
    occupancy: p.occupancy != null ? String(p.occupancy) : '',
    rent_amount: p.rent_amount != null ? String(p.rent_amount) : '',
    federal_id: p.federal_id != null ? String(p.federal_id) : '',
    pmc_invite: p.pmc_invite != null ? String(p.pmc_invite) : '1',
    main_contact_person: p.main_contact_person != null ? String(p.main_contact_person) : '',
    policy_id: p.policy_id != null ? String(p.policy_id) : '',
    website_url: p.website_url != null ? String(p.website_url) : '',
    latitude: p.latitude != null ? String(p.latitude) : '',
    longitude: p.longitude != null ? String(p.longitude) : '',
    email_frequency:
      p.email_frequency != null && String(p.email_frequency) !== ''
        ? String(p.email_frequency)
        : 'MONTHLY',
    activate_api: p.activate_api != null ? String(p.activate_api) : '',
    disable_uc: p.disable_uc != null ? String(p.disable_uc) : '0',
    range: 'range_ytd',
    allow_dynamic_access_fee: coerceAllowDynamicAccessFee(p.allow_dynamic_access_fee),
  }
}

/** Build filtered POST body (before FormData) — Angular `getFilterValue` + `validateData` parity. */
export function buildPropertyUpdatePayload(params: {
  data: PropertyEditFormValues
  propertyDetails: Record<string, unknown> | null
  execSummaryData: Record<string, string>
  isLibertyRole: boolean
  skipAln: boolean
}): Record<string, unknown> {
  const { data, propertyDetails, execSummaryData, isLibertyRole, skipAln } = params

  const mailingChecked = Boolean(data.is_mailing_address)

  const raw: Record<string, unknown> = {
    fk_company_id: data.fk_company_id,
    property_name: (data.property_name || '').trim(),
    property_alias_name: isLibertyRole
      ? (data.property_alias_name || '').trim()
      : String(propertyDetails?.property_alias_name ?? '').trim(),
    aln_ref_id: (data.aln_ref_id || '').trim(),
    contact_name: (data.contact_name || '').trim(),
    contact_phone: (data.contact_phone || '').replace(/\D/g, ''),
    property_office_phone: (data.property_office_phone || '').replace(/\D/g, ''),
    property_address: (data.property_address || '').trim(),
    property_address2: (data.property_address2 || '').trim(),
    property_city: (data.property_city || '').trim(),
    property_state: data.property_state,
    property_zipcode: (data.property_zipcode || '').trim(),
    mailing_property_address: mailingChecked ? (data.mailing_property_address || '').trim() : '',
    mailing_property_address2: mailingChecked ? (data.mailing_property_address2 || '').trim() : '',
    mailing_property_city: mailingChecked ? (data.mailing_property_city || '').trim() : '',
    mailing_property_state: mailingChecked ? data.mailing_property_state : '',
    mailing_property_zipcode: mailingChecked ? (data.mailing_property_zipcode || '').trim() : '',
    units_count: data.units_count,
    listed: data.listed,
    listed_ids: mapPropertyListedIds(
      Array.isArray(data.listed_ids) ? data.listed_ids : propertyDetails?.listed_ids,
    ),
    occupancy: data.occupancy ?? '',
    rent_amount: data.rent_amount != null ? String(data.rent_amount).replace(/[^0-9.]+/g, '') : '',
    pmc_invite: data.pmc_invite ?? '1',
    main_contact_person: (data.main_contact_person || '').trim(),
    policy_id: (data.policy_id || '').trim(),
    website_url: ((data.website_url ?? (propertyDetails?.website_url as string | undefined) ?? '') as string).trim(),
    latitude: data.latitude ?? '',
    longitude: data.longitude ?? '',
    property_mgmt_system: (propertyDetails?.property_mgmt_system as string) ?? '',
    resident_screening: (propertyDetails?.resident_screening as string) ?? '',
    email_frequency:
      data.email_frequency != null && String(data.email_frequency).trim() !== ''
        ? String(data.email_frequency).trim()
        : propertyDetails?.email_frequency != null && String(propertyDetails.email_frequency).trim() !== ''
        ? String(propertyDetails.email_frequency).trim()
        : 'MONTHLY',
    activate_api: data.activate_api ?? '',
    disable_uc: data.disable_uc ?? '0',
    range: data.range ?? 'range_ytd',
    ...execSummaryData,
  }

  const payload: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(raw)) {
    if (PAYMENT_FIELDS.has(key)) continue
    const trimmed = typeof value === 'string' ? value.trim() : value
    if (!trimmed) continue
    payload[key] = trimmed
  }

  payload.federal_id = (data.federal_id ?? '').toString().trim()
  payload.is_mailing_address = data.is_mailing_address ? '1' : '0'

  if (skipAln) {
    payload.is_skip_aln = '1'
  }

  // Angular sets this after getFilterValue when eviction is Yes so the clear is not dropped.
  if (String(execSummaryData.eviction_credit_report ?? '') === '1') {
    payload.time_frame_period = ''
  }

  const invitationApiKey = String(
    propertyDetails?.invitation_api_key ?? propertyDetails?.invitationApiKey ?? '',
  ).trim()
  if (invitationApiKey) {
    payload.invitation_api_key = invitationApiKey
  }

  return payload
}
